import { useEffect, useState } from "react";

import { subscribeNotifications, unsubscribeNotifications } from "API/calls";
import Switch from "components/elements/Switch";
import { usePublicDataContext } from "context/PublicDataContext";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  convertSubscriptionToBackendFormat,
  getExistingSubscription,
  isPushNotificationSupported,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from "utils/pushNotifications";

const PushSwitch = () => {
  const { t } = useTranslation();
  const { vapidPublicKey } = usePublicDataContext();
  const { enqueueSnackbar } = useSnackbar();
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(true);

  useEffect(() => {
    const checkPushStatus = async () => {
      if (!isPushNotificationSupported()) {
        setIsSupported(false);
        return;
      }

      try {
        const subscription = await getExistingSubscription();
        setIsPushEnabled(!!subscription);
      } catch (error) {
        console.error("Error checking push status:", error);
      }
    };

    checkPushStatus();
  }, []);

  const handlePushToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!isSupported || !vapidPublicKey) {
      enqueueSnackbar(t("notifications.push.notSupported"), { variant: "error" });
      return;
    }

    setIsLoading(true);
    const isSubscribing = event.target.checked;

    try {
      if (isSubscribing) {
        const subscription = await subscribeToPushNotifications(vapidPublicKey);
        const backendFormat = convertSubscriptionToBackendFormat(subscription);

        await subscribeNotifications(backendFormat);

        setIsPushEnabled(true);
        enqueueSnackbar(t("notifications.push.enabled"), { variant: "success" });
      } else {
        const subscription = await getExistingSubscription();
        if (subscription) {
          const backendFormat = convertSubscriptionToBackendFormat(subscription);
          await unsubscribeNotifications(backendFormat.endpoint);
        }

        await unsubscribeFromPushNotifications();

        setIsPushEnabled(false);
        enqueueSnackbar(t("notifications.push.disabled"), { variant: "success" });
      }
    } catch (error: any) {
      if (isSubscribing) {
        try {
          await unsubscribeFromPushNotifications();
        } catch (unsubError) {
          console.error("Error cleaning up subscription:", unsubError);
        }
      }

      let errorMessage = t("notifications.push.error");

      if (error.message === "Notification permission denied") {
        errorMessage = t("notifications.push.permissionDenied");
      } else if (error.message === "Push notifications are not supported in this browser") {
        errorMessage = t("notifications.push.notSupported");
      }

      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return <Switch checked={isPushEnabled} onChange={handlePushToggle} disabled={isLoading || !isSupported || !vapidPublicKey} />;
};
export default PushSwitch;
